import { useState, useMemo, useCallback, memo, useEffect, useRef, type ReactNode } from 'react'
import { ArrowLeft, Search, Send, CreditCard, X, Users, UtensilsCrossed, WifiOff, Minus, Plus, StickyNote, Check, Scale } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Skeleton } from '@/components/ui/Skeleton'
import { Modal } from '@/components/ui/Modal'
import { POSCheckoutModal } from './POSCheckoutModal'
import { POSVisualKeyboard, POSVisualKeyboardField, POSVisualNumpadField } from './POSVisualInput'
import { cn } from '@/lib/utils'
import {
  usePOSOrders,
  usePOSOrder,
  useCreatePOSOrder,
  useSendToKitchen,
  useVoidOrder,
  useRemoveOrderItem,
  useAddOrderItem,
  useUpdateOrderItemQty,
  useUpdateOrderItem,
  useMenuCategories,
  useMenuItems,
  TAX_CLASS_RATE,
} from '@/lib/queries/usePOS'
import { usePOSPreferencesStore, getPOSRestaurantPreferences, type POSOrderTypeKey, type POSOrderTypeOption } from '@/lib/store/usePOSPreferencesStore'
import { useAppStore } from '@/lib/store/useAppStore'
import { formatPOSMoney, usePOSCurrency } from '@/lib/pos/currency'
import { searchMenuItems } from '@/lib/pos/menuSearch'
import type { MenuItem, POSOrder, POSOrderItem, POSServiceMode } from '@/lib/types/pos'
import { toast } from 'sonner'

// ── Colour palette for product tiles (one colour per category) ────────────────

const TILE_COLORS = [
  'bg-orange-500 active:bg-orange-400',
  'bg-rose-500   active:bg-rose-400',
  'bg-violet-600 active:bg-violet-500',
  'bg-blue-600   active:bg-blue-500',
  'bg-teal-600   active:bg-teal-500',
  'bg-emerald-600 active:bg-emerald-500',
  'bg-lime-600   active:bg-lime-500',
  'bg-amber-500  active:bg-amber-400',
  'bg-pink-600   active:bg-pink-500',
  'bg-sky-600    active:bg-sky-500',
  'bg-indigo-600 active:bg-indigo-500',
  'bg-red-600    active:bg-red-500',
]

const STATUS_DOT: Record<string, string> = {
  pending:     'bg-[var(--color-text-muted)]',
  sent:        'bg-amber-400',
  in_progress: 'bg-orange-400',
  ready:       'bg-green-400',
  served:      'bg-blue-400',
  voided:      'bg-red-400',
}

const ORDER_STATUS_COLOR: Record<string, string> = {
  open:           'bg-green-500/15 text-green-400',
  sent:           'bg-amber-500/15 text-amber-400',
  partially_paid: 'bg-amber-500/15 text-amber-400',
  paid:           'bg-blue-500/15 text-blue-400',
  voided:         'bg-zinc-500/15 text-[var(--color-text-muted)]',
  closed:         'bg-zinc-500/15 text-[var(--color-text-muted)]',
}

const SERVICE_MODE_LABEL: Record<'table' | 'counter' | 'takeaway', string> = {
  table: 'Table service',
  counter: 'Counter sale',
  takeaway: 'Takeaway',
}

function orderTypeToServiceMode(key: POSOrderTypeKey, fallback: POSServiceMode): POSServiceMode {
  if (key === 'takeaway' || key === 'glovo') return 'takeaway'
  return fallback === 'table' ? 'table' : 'counter'
}

function notePrefixForOrderType(option: POSOrderTypeOption | undefined) {
  if (!option || option.key === 'dine_in') return undefined
  return `Order type: ${option.label}`
}


function tileSwatch(color: string) {
  return color.split(/\s+/)[0] ?? 'bg-zinc-600'
}

// ── Sub-components ────────────────────────────────────────────────────────────

const ProductTile = memo(function ProductTile({
  item,
  color,
  money,
  onTileClick,
}: {
  item: MenuItem
  color: string
  money: (amount: number) => string
  onTileClick: (item: MenuItem) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onTileClick(item)}
      disabled={!item.isAvailable}
      aria-label={`${item.name}, ${money(item.basePrice)}${item.isAvailable ? '' : ', sold out'}`}
      className={[
        'relative flex min-h-[96px] touch-manipulation flex-col justify-end p-3 rounded-[var(--radius-md)] text-white select-none',
        'aspect-[4/3] transition-transform',
        item.isAvailable ? `${color} cursor-pointer active:scale-[0.97]` : 'bg-zinc-700 opacity-40 cursor-not-allowed',
      ].join(' ')}
    >
      {item.category?.name && (
        <span className="mb-0.5 text-[10px] font-medium uppercase tracking-wide text-white/70 text-left truncate">
          {item.category.name}
        </span>
      )}
      <span className="font-semibold text-sm leading-tight line-clamp-2 text-left">{item.name}</span>
      <span className="mt-0.5 flex flex-wrap items-center gap-1 text-left text-xs text-white/75">
        <span>{money(item.basePrice)}</span>
        {item.activePromotion && (
          <>
            <span className="text-[10px] line-through text-white/55">
              {money(item.activePromotion.regularPrice)}
            </span>
            <span className="rounded-full bg-black/20 px-1.5 py-0.5 text-[10px] font-semibold text-white">
              {item.activePromotion.label || 'Promo'}
            </span>
          </>
        )}
      </span>
      {!item.isAvailable && (
        <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white/60">
          Sold out
        </span>
      )}
    </button>
  )
})

const CategoryFilterButton = memo(function CategoryFilterButton({
  label,
  swatchClass,
  active,
  onClick,
}: {
  label: string
  swatchClass: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'flex min-h-11 min-w-0 items-center gap-2 rounded-[var(--radius-md)] border px-2.5 py-2 text-left text-xs font-semibold transition-colors',
        active
          ? 'border-[var(--color-accent-amber)] bg-[var(--color-accent-amber-dim)] text-[var(--color-text-primary)]'
          : 'border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] hover:border-[var(--color-border-default)] hover:text-[var(--color-text-primary)]',
      )}
    >
      <span className={cn('h-3 w-3 shrink-0 rounded-full ring-1 ring-white/20', swatchClass)} />
      <span className="min-w-0 truncate">{label}</span>
    </button>
  )
})

const OrderRow = memo(function OrderRow({
  item,
  selected,
  saving,
  money,
  onSelect,
  onQuantityChange,
  onEditNote,
  onEditWeight,
  onRemove,
}: {
  item: POSOrderItem
  selected: boolean
  saving: boolean
  money: (amount: number) => string
  onSelect: () => void
  onQuantityChange: (quantity: number) => void
  onEditNote: () => void
  onEditWeight: () => void
  onRemove: () => void
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onSelect()
        }
      }}
      aria-pressed={selected}
      className={cn(
      'group flex min-h-10 items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1 transition-colors',
      selected ? 'bg-[var(--color-accent-amber-dim)] ring-1 ring-[var(--color-accent-amber)]/40' : 'hover:bg-[var(--color-bg-elevated)]',
    )}>
      <div className={`h-2 w-2 rounded-full shrink-0 ${STATUS_DOT[item.status] ?? 'bg-gray-500'}`} />
      <span className="flex-1 text-sm text-[var(--color-text-primary)] truncate min-w-0">
        <span className="text-[var(--color-text-muted)] mr-1">
          {item.weightGrams != null
            ? item.weightGrams % 1000 === 0 ? `${item.weightGrams / 1000} kg` : `${item.weightGrams} g`
            : `${item.quantity}×`}
        </span>
        {item.menuItem?.name}
        {item.variant && (
          <span className="text-[var(--color-text-muted)] ml-1 text-xs">({item.variant.name})</span>
        )}
        {item.notes && (
          <span className="text-[var(--color-text-muted)] ml-1 text-xs italic">{item.notes}</span>
        )}
      </span>
      {selected && (
        <div className="flex shrink-0 items-center gap-1 rounded-[var(--radius-sm)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] p-0.5">
          {item.weightGrams == null && <button
            type="button"
            aria-label="Decrease item quantity"
            onClick={(event) => {
              event.stopPropagation()
              onQuantityChange(Math.max(1, item.quantity - 1))
            }}
            disabled={item.quantity <= 1 || saving}
            className="flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Minus className="h-3.5 w-3.5" />
          </button>}
          {item.weightGrams == null && <button
            type="button"
            aria-label="Increase item quantity"
            onClick={(event) => {
              event.stopPropagation()
              onQuantityChange(item.quantity + 1)
            }}
            disabled={saving}
            className="flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>}
          <button
            type="button"
            aria-label={`Edit note for ${item.menuItem?.name ?? 'item'}`}
            onClick={(event) => {
              event.stopPropagation()
              onEditNote()
            }}
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm)] transition-colors hover:bg-[var(--color-bg-elevated)]',
              item.notes ? 'text-[var(--color-accent-amber)]' : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
            )}
          >
            <StickyNote className="h-3.5 w-3.5" />
          </button>
          {item.weightGrams != null && (
            <button
              type="button"
              aria-label={`Confirm weighed amount for ${item.menuItem?.name ?? 'item'}`}
              title="Enter actual scale weight"
              onClick={(event) => { event.stopPropagation(); onEditWeight() }}
              disabled={saving}
              className="flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-accent-amber)] transition-colors hover:bg-[var(--color-bg-elevated)] disabled:opacity-50"
            >
              <Scale className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
      <span className="text-sm font-medium text-[var(--color-text-primary)] shrink-0">
        {money(item.unitPrice * item.quantity)}
        {item.weightIsEstimate && <span className="ml-1 text-[10px] font-semibold uppercase text-amber-400">est.</span>}
      </span>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          onRemove()
        }}
        aria-label={`Remove ${item.menuItem?.name ?? 'item'}`}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-[var(--color-text-muted)] transition-all hover:bg-red-500/10 hover:text-[var(--color-accent-red)] sm:h-6 sm:w-6 sm:opacity-0 sm:group-hover:opacity-100"
        title="Remove item"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
})

/** Stable wrapper so each OrderRow gets a stable onRemove callback */
const OrderItemsList = memo(function OrderItemsList({
  items,
  orderDocumentId,
  selectedItemId,
  savingItem,
  money,
  onSelectItem,
  onQuantityChange,
  onEditNote,
  onEditWeight,
  onRemove,
}: {
  items: POSOrderItem[]
  orderDocumentId: string
  selectedItemId: string | null
  savingItem: boolean
  money: (amount: number) => string
  onSelectItem: (documentId: string) => void
  onQuantityChange: (documentId: string, quantity: number) => void
  onEditNote: (documentId: string) => void
  onEditWeight: (documentId: string) => void
  onRemove: (args: { documentId: string; orderDocumentId: string }) => void
}) {
  return (
    <>
      {items.map((item) => (
        <OrderRowWrapper
          key={item.documentId}
          item={item}
          orderDocumentId={orderDocumentId}
          selected={selectedItemId === item.documentId}
          savingItem={savingItem}
          money={money}
          onSelectItem={onSelectItem}
          onQuantityChange={onQuantityChange}
          onEditNote={onEditNote}
          onEditWeight={onEditWeight}
          onRemove={onRemove}
        />
      ))}
    </>
  )
})

const OrderRowWrapper = memo(function OrderRowWrapper({
  item,
  orderDocumentId,
  selected,
  savingItem,
  money,
  onSelectItem,
  onQuantityChange,
  onEditNote,
  onEditWeight,
  onRemove,
}: {
  item: POSOrderItem
  orderDocumentId: string
  selected: boolean
  savingItem: boolean
  money: (amount: number) => string
  onSelectItem: (documentId: string) => void
  onQuantityChange: (documentId: string, quantity: number) => void
  onEditNote: (documentId: string) => void
  onEditWeight: (documentId: string) => void
  onRemove: (args: { documentId: string; orderDocumentId: string }) => void
}) {
  const handleRemove = useCallback(
    () => onRemove({ documentId: item.documentId, orderDocumentId }),
    [item.documentId, orderDocumentId, onRemove],
  )
  const handleSelect = useCallback(() => onSelectItem(item.documentId), [item.documentId, onSelectItem])
  const handleQuantityChange = useCallback(
    (quantity: number) => onQuantityChange(item.documentId, quantity),
    [item.documentId, onQuantityChange],
  )
  const handleEditNote = useCallback(() => onEditNote(item.documentId), [item.documentId, onEditNote])
  const handleEditWeight = useCallback(() => onEditWeight(item.documentId), [item.documentId, onEditWeight])

  return (
    <OrderRow
      item={item}
      selected={selected}
      saving={savingItem}
      money={money}
      onSelect={handleSelect}
      onQuantityChange={handleQuantityChange}
      onEditNote={handleEditNote}
      onEditWeight={handleEditWeight}
      onRemove={handleRemove}
    />
  )
})

// ── Main terminal ─────────────────────────────────────────────────────────────

function FocusedOrderItemEditor({
  item,
  noteDraft,
  saving,
  keyboardOpen,
  onKeyboardOpenChange,
  onNoteDraftChange,
  onSaveNote,
  onClose,
}: {
  item: POSOrderItem
  noteDraft: string
  saving: boolean
  keyboardOpen: boolean
  onKeyboardOpenChange: (open: boolean) => void
  onNoteDraftChange: (value: string) => void
  onSaveNote: () => void
  onClose: () => void
}) {
  const inputMode = usePOSPreferencesStore((state) => state.inputMode)

  return (
    <div className="shrink-0 border-t border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] p-3">
      <div className="rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-base)] p-3">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
              <StickyNote className="h-3.5 w-3.5" />
              Selected item
            </p>
            <p className="truncate text-sm font-semibold text-[var(--color-text-primary)]">{item.menuItem?.name}</p>
          </div>
          <button
            type="button"
            aria-label="Close item note editor"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="space-y-2">
          <textarea
            aria-label="Item note"
            readOnly={inputMode === 'touchscreen'}
            value={noteDraft}
            onChange={(event) => onNoteDraftChange(event.target.value)}
            onClick={() => inputMode === 'touchscreen' && onKeyboardOpenChange(true)}
            rows={3}
            placeholder="Add kitchen note..."
            className="w-full resize-none rounded-[var(--radius-sm)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:ring-1 focus:ring-[var(--color-accent-amber)]/40"
          />
          {inputMode === 'touchscreen' && keyboardOpen && (
            <POSVisualKeyboard value={noteDraft} onChange={onNoteDraftChange} />
          )}
          <div className="flex gap-2">
            {inputMode === 'touchscreen' && (
              <Button size="sm" variant="secondary" className="justify-center" onClick={() => onKeyboardOpenChange(!keyboardOpen)}>
                {keyboardOpen ? 'Hide keyboard' : 'Show keyboard'}
              </Button>
            )}
            <Button size="sm" className="flex-1 justify-center" loading={saving} onClick={onSaveNote}>
              <Check className="h-4 w-4" />
              Save note
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface Props {
  tableDocumentId?: string | null
  tableName: string
  onBack: () => void
  serviceMode?: 'table' | 'counter' | 'takeaway'
  showBack?: boolean
  headerActions?: ReactNode
}

export function POSTerminal({
  tableDocumentId = null,
  tableName,
  onBack,
  serviceMode = tableDocumentId ? 'table' : 'counter',
  showBack = true,
  headerActions,
}: Props) {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null)
  const currency = usePOSCurrency()
  const money = useCallback((amount: number) => formatPOSMoney(amount, currency), [currency])
  const [search, setSearch] = useState('')
  const [covers, setCovers] = useState(2)
  const [showCheckout, setShowCheckout] = useState(false)
  const [checkoutOrderDocumentId, setCheckoutOrderDocumentId] = useState<string | null>(null)
  const [variantModal, setVariantModal] = useState<MenuItem | null>(null)
  const [weightEditorItem, setWeightEditorItem] = useState<POSOrderItem | null>(null)
  const [weightDraft, setWeightDraft] = useState('')
  const [pickedVariantId, setPickedVariantId] = useState<string>('')
  const coversEnabled = usePOSPreferencesStore((s) => s.coversEnabled)
  const restaurantId = useAppStore((s) => s.restaurantId)
  const restaurantPreferences = usePOSPreferencesStore((s) => s.restaurantPreferences)
  const restaurantPrefs = useMemo(
    () => getPOSRestaurantPreferences(restaurantPreferences, restaurantId),
    [restaurantPreferences, restaurantId],
  )
  const [selectedOrderType, setSelectedOrderType] = useState<POSOrderTypeKey>('dine_in')
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  const pendingSelectionRef = useRef<string | null>(null)
  const [noteDraft, setNoteDraft] = useState('')
  const enabledOrderTypeOptions = restaurantPrefs.orderTypeOptions.filter((option) => option.enabled)
  const orderTypeSelectorEnabled = restaurantPrefs.orderTypeToggleEnabled && enabledOrderTypeOptions.length > 0
  const selectedOrderTypeOption =
    enabledOrderTypeOptions.find((option) => option.key === selectedOrderType) ?? enabledOrderTypeOptions[0]
  const [noteEditorItemId, setNoteEditorItemId] = useState<string | null>(null)
  const [noteKeyboardOpen, setNoteKeyboardOpen] = useState(false)
  const effectiveServiceMode = orderTypeSelectorEnabled
    ? orderTypeToServiceMode(selectedOrderTypeOption?.key ?? 'dine_in', serviceMode)
    : serviceMode
  const orderTypeNote = orderTypeSelectorEnabled ? notePrefixForOrderType(selectedOrderTypeOption) : undefined

  // ── Data ──────────────────────────────────────────────────────────────────

  const { data: ordersResp } = usePOSOrders(undefined, { refetchInterval: 30_000 })
  const allOrders: POSOrder[] = (ordersResp as any)?.data ?? []

  const activeOrder = useMemo(() => {
    const candidates = allOrders
      .filter(
        (o) => {
          const isActive = o.status !== 'voided' && o.status !== 'paid' && o.status !== 'closed'
          if (!isActive) return false
          if (effectiveServiceMode === 'table') return !!tableDocumentId && o.table?.documentId === tableDocumentId
          return (o.serviceMode ?? (o.table?.documentId ? 'table' : 'counter')) === effectiveServiceMode && !o.table?.documentId
        },
      )
      .sort((a, b) => (b.openedAt > a.openedAt ? 1 : -1))
    return candidates[0] ?? null
  }, [allOrders, effectiveServiceMode, tableDocumentId])

  const { data: fullOrderResp } = usePOSOrder(activeOrder?.documentId ?? null)
  const order: POSOrder | null = (fullOrderResp as any)?.data ?? null

  const { data: catsResp } = useMenuCategories()
  const categories: any[] = (catsResp as any)?.data ?? []

  const { data: itemsResp, isLoading: categoryItemsLoading } = useMenuItems(selectedCategoryId)
  const categoryItems: MenuItem[] = (itemsResp as any)?.data ?? []
  const { data: allItemsResp, isLoading: allItemsLoading } = useMenuItems()
  const allItems: MenuItem[] = (allItemsResp as any)?.data ?? []
  const isSearching = search.trim().length > 0
  const itemsLoading = isSearching ? allItemsLoading : categoryItemsLoading

  // ── Mutations ─────────────────────────────────────────────────────────────

  const createOrder  = useCreatePOSOrder()
  const addItem      = useAddOrderItem()
  const updateQty    = useUpdateOrderItemQty()
  const updateItem   = useUpdateOrderItem()
  const removeItem   = useRemoveOrderItem()
  const sendKitchen  = useSendToKitchen()
  const voidOrderMut = useVoidOrder()

  // ── Derived ───────────────────────────────────────────────────────────────

  const catColorMap = useMemo(() => {
    const m = new Map<string, string>()
    categories.forEach((c: any, i: number) => m.set(c.documentId, TILE_COLORS[i % TILE_COLORS.length]))
    return m
  }, [categories])

  const filteredItems = useMemo(() => {
    return isSearching ? searchMenuItems(allItems, search) : categoryItems
  }, [allItems, categoryItems, isSearching, search])

  const orderItems = order?.orderItems?.filter((i) => i.status !== 'voided') ?? []
  const pendingCount = orderItems.filter((i) => i.status === 'pending').length
  const totalItemCount = orderItems.reduce((s, i) => s + i.quantity, 0)
  const grossTotal   = orderItems.reduce((s, i) => s + i.unitPrice * i.quantity, 0)
  const isSyncPending = !!activeOrder?.documentId.startsWith('temp-')
  const serviceLabel = orderTypeSelectorEnabled && selectedOrderTypeOption
    ? selectedOrderTypeOption.label
    : SERVICE_MODE_LABEL[effectiveServiceMode]
  const orderCovers = coversEnabled ? covers : undefined
  const noteEditorItem = orderItems.find((item) => item.documentId === noteEditorItemId) ?? null

  useEffect(() => {
    if (!selectedOrderTypeOption && enabledOrderTypeOptions[0]) {
      setSelectedOrderType(enabledOrderTypeOptions[0].key)
    }
  }, [enabledOrderTypeOptions, selectedOrderTypeOption])

  useEffect(() => {
    if (!orderItems.length) {
      setSelectedItemId(null)
      setNoteEditorItemId(null)
      return
    }
    const pendingSelection = pendingSelectionRef.current
    if (pendingSelection) {
      if (orderItems.some((item) => item.documentId === pendingSelection)) {
        setSelectedItemId(pendingSelection)
        pendingSelectionRef.current = null
      }
    } else if (!selectedItemId || !orderItems.some((item) => item.documentId === selectedItemId)) {
      setSelectedItemId(orderItems[0].documentId)
    }
    if (noteEditorItemId && !orderItems.some((item) => item.documentId === noteEditorItemId)) {
      setNoteEditorItemId(null)
      setNoteKeyboardOpen(false)
    }
  }, [orderItems, selectedItemId, noteEditorItemId])

  useEffect(() => {
    if (noteEditorItem) setNoteDraft(noteEditorItem.notes ?? '')
  }, [noteEditorItem?.documentId, noteEditorItem?.notes])

  // ── Handlers ──────────────────────────────────────────────────────────────

  const doAddItem = useCallback(
    async (item: MenuItem, variantId?: string) => {
      let orderId = activeOrder?.documentId

      if (!orderId) {
        try {
          const res = await createOrder.mutateAsync({ tableDocumentId, covers: orderCovers, serviceMode: effectiveServiceMode, notes: orderTypeNote })
          orderId = (res as any)?.data?.documentId
          if (!orderId) { toast.error('Could not open order'); return }
        } catch {
          return
        }
      }

      // If a matching line already exists (same item + variant, not sent/voided),
      // increment its quantity instead of adding a duplicate row.
      const existingLine = order?.orderItems?.find(
        (oi) =>
          oi.status !== 'voided' &&
          oi.status !== 'sent' &&
          oi.status !== 'in_progress' &&
          oi.status !== 'ready' &&
          oi.status !== 'served' &&
          oi.menuItem?.documentId === item.documentId &&
          (oi.variant?.documentId ?? undefined) === (variantId ?? undefined),
      )

      if (existingLine) {
        updateQty.mutate({ documentId: existingLine.documentId, quantity: existingLine.quantity + 1 })
        setSelectedItemId(existingLine.documentId)
        return
      }

      const variant    = variantId ? item.variants.find((v) => v.documentId === variantId) : null
      const unitPrice  = item.basePrice + (variant?.priceDelta ?? 0)
      const taxRate    = TAX_CLASS_RATE[item.taxClass] ?? 0

      addItem.mutate(
        {
          orderDocumentId:    orderId,
          menuItemDocumentId: item.documentId,
          menuItemSnapshot:   item,
          variantDocumentId:  variantId,
          quantity:  1,
          unitPrice,
          taxRate,
        },
        {
          onSuccess: (res: any) => {
            const documentId = res?.data?.documentId
            if (documentId) {
              pendingSelectionRef.current = documentId
              setSelectedItemId(documentId)
            }
          },
        },
      )
    },
    [activeOrder, order, createOrder, addItem, updateQty, tableDocumentId, orderCovers, effectiveServiceMode, orderTypeNote],
  )

  const handleTileClick = useCallback(
    (item: MenuItem) => {
      if (!item.isAvailable) return
      if (item.variants.length > 0) {
        setPickedVariantId(item.variants[0].documentId)
        setVariantModal(item)
        return
      }
      doAddItem(item)
    },
    [doAddItem],
  )

  const handleVariantConfirm = async () => {
    if (!variantModal) return
    try {
      await doAddItem(variantModal, pickedVariantId || undefined)
      setVariantModal(null)
    } catch {
      // keep modal open on failure
    }
  }

  const handleOrderItemQuantityChange = useCallback(
    (documentId: string, quantity: number) => {
      updateItem.mutate({ documentId, patch: { quantity } })
    },
    [updateItem],
  )

  const handleEditOrderItemNote = useCallback(
    (documentId: string) => {
      const item = orderItems.find((orderItem) => orderItem.documentId === documentId)
      setSelectedItemId(documentId)
      setNoteEditorItemId(documentId)
      setNoteKeyboardOpen(false)
      setNoteDraft(item?.notes ?? '')
    },
    [orderItems],
  )

  const handleEditOrderItemWeight = useCallback((documentId: string) => {
    const item = orderItems.find((orderItem) => orderItem.documentId === documentId)
    if (!item || item.weightGrams == null) return
    setSelectedItemId(documentId)
    setWeightEditorItem(item)
    setWeightDraft(String(item.weightGrams))
  }, [orderItems])

  const handleCloseNoteEditor = useCallback(() => {
    setNoteEditorItemId(null)
    setNoteKeyboardOpen(false)
  }, [])

  const handleVoid = () => {
    if (!activeOrder) return
    if (isSyncPending) {
      toast.error('Sync pending - void after the order syncs')
      return
    }
    if (!confirm(`Void entire order for ${tableName}?`)) return
    voidOrderMut.mutate(activeOrder.documentId, { onSuccess: onBack })
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full bg-[var(--color-bg-base)]">

      {/* ── Top bar ──────────────────────────────────────────────────────── */}
      <div className="shrink-0 flex min-h-14 flex-wrap items-center gap-2 px-3 py-2 sm:gap-3 sm:px-4 border-b border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)]">
        {showBack && (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors pr-3 border-r border-[var(--color-border-subtle)]"
          >
            <ArrowLeft className="h-4 w-4" />
            Tables
          </button>
        )}

        <span className="min-w-0 truncate font-semibold text-[var(--color-text-primary)]">{tableName}</span>
        {headerActions && <div className="shrink-0">{headerActions}</div>}
        {orderTypeSelectorEnabled && (
          <div
            className="flex shrink-0 items-center gap-1 rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-1"
            aria-label="POS order type"
          >
            {enabledOrderTypeOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                aria-pressed={selectedOrderTypeOption?.key === option.key}
                disabled={!!activeOrder}
                onClick={() => setSelectedOrderType(option.key)}
                className={cn(
                  'rounded-[var(--radius-sm)] px-2 py-1 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-70',
                  selectedOrderTypeOption?.key === option.key
                    ? 'bg-[var(--color-accent-amber)] text-black'
                    : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
        <span className="shrink-0 rounded-full bg-[var(--color-bg-elevated)] px-2 py-0.5 text-xs font-medium text-[var(--color-text-secondary)]">
          {serviceLabel}
        </span>

        {activeOrder && (
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ORDER_STATUS_COLOR[activeOrder.status] ?? ''}`}>
            {activeOrder.status.replace('_', ' ')}
          </span>
        )}

        {isSyncPending && (
          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-500/15 text-amber-400">
            Sync pending
          </span>
        )}

        <div className="ml-auto flex min-w-0 items-center gap-2 sm:gap-3">
          {/* Covers stepper — only when no order yet */}
          {coversEnabled && !activeOrder && (
            <div className="flex items-center gap-1.5 text-sm">
              <Users className="h-4 w-4 text-[var(--color-text-muted)]" />
              <button
                type="button"
                onClick={() => setCovers((c) => Math.max(1, c - 1))}
                disabled={createOrder.isPending}
                aria-label="Decrease covers"
                className="h-6 w-6 rounded bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] text-base font-bold leading-none disabled:opacity-40 disabled:pointer-events-none"
              >
                −
              </button>
              <span className="w-5 text-center font-medium text-[var(--color-text-primary)]">{covers}</span>
              <button
                type="button"
                onClick={() => setCovers((c) => c + 1)}
                disabled={createOrder.isPending}
                aria-label="Increase covers"
                className="h-6 w-6 rounded bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] text-base font-bold leading-none disabled:opacity-40 disabled:pointer-events-none"
              >
                +
              </button>
              <span className="hidden text-xs text-[var(--color-text-muted)] sm:inline">covers</span>
            </div>
          )}

          {activeOrder && !['paid', 'voided', 'closed'].includes(activeOrder.status) && (
            <button
              type="button"
              onClick={handleVoid}
              disabled={isSyncPending}
              title={isSyncPending ? 'Available after this order syncs' : undefined}
              className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-accent-red)] transition-colors px-2 py-1 rounded"
            >
              Void order
            </button>
          )}
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0 overflow-hidden">

        {/* ── Left: order panel ─────────────────────────────────────────── */}
        <div className="w-full lg:w-[340px] xl:w-[380px] lg:shrink-0 flex flex-col border-b lg:border-b-0 lg:border-r border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] min-h-[140px] max-h-[42%] lg:max-h-none lg:min-h-0">

          <div className="shrink-0 border-b border-[var(--color-border-subtle)] px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">Current ticket</p>
                <p className="truncate text-sm font-semibold text-[var(--color-text-primary)]">
                  {activeOrder ? tableName : 'No open order'}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-xs text-[var(--color-text-muted)]">Items</p>
                <p className="text-sm font-semibold text-[var(--color-text-primary)]">{totalItemCount}</p>
              </div>
            </div>
          </div>

          {/* Items list */}
          <div className="flex-1 overflow-y-auto py-2">
            {!activeOrder ? (
              <div className="flex flex-col items-center justify-center h-full gap-3 px-6 text-center">
                <UtensilsCrossed className="h-10 w-10 text-[var(--color-text-muted)]" />
                <p className="text-sm text-[var(--color-text-muted)]">
                  Tap any product to open an order
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  loading={createOrder.isPending}
                  onClick={() =>
                    createOrder.mutate({
                      tableDocumentId,
                      covers: orderCovers,
                      serviceMode: effectiveServiceMode,
                      notes: orderTypeNote,
                    })
                  }
                >
                  {coversEnabled ? `Open order (${covers} covers)` : 'Open order'}
                </Button>
              </div>
            ) : orderItems.length === 0 ? (
              <div className="flex items-center justify-center h-full text-sm text-[var(--color-text-muted)]">
                No items yet — tap a product
              </div>
            ) : (
              <OrderItemsList
                items={orderItems}
                orderDocumentId={order!.documentId}
                selectedItemId={selectedItemId}
                savingItem={updateItem.isPending}
                money={money}
                onSelectItem={setSelectedItemId}
                onQuantityChange={handleOrderItemQuantityChange}
                onEditNote={handleEditOrderItemNote}
                onEditWeight={handleEditOrderItemWeight}
                onRemove={removeItem.mutate}
              />
            )}
          </div>

          {noteEditorItem && (
            <FocusedOrderItemEditor
              item={noteEditorItem}
              noteDraft={noteDraft}
              saving={updateItem.isPending}
              keyboardOpen={noteKeyboardOpen}
              onKeyboardOpenChange={setNoteKeyboardOpen}
              onNoteDraftChange={setNoteDraft}
              onSaveNote={() =>
                updateItem.mutate(
                  { documentId: noteEditorItem.documentId, patch: { notes: noteDraft.trim() } },
                  { onSuccess: handleCloseNoteEditor },
                )
              }
              onClose={handleCloseNoteEditor}
            />
          )}

          {/* Totals */}
          {activeOrder && (
            <div className="shrink-0 px-4 py-3 border-t border-[var(--color-border-subtle)]">
              <div className="flex justify-between text-base font-bold text-[var(--color-text-primary)]">
                <span>Total</span>
                <span>{money(grossTotal)}</span>
              </div>
              {orderItems.length > 0 && (
                <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                  {totalItemCount} item{totalItemCount !== 1 ? 's' : ''}
                  {order?.covers ? ` · ${order.covers} covers` : ''}
                </p>
              )}
            </div>
          )}

          {activeOrder && isSyncPending && (
            <div className="mx-3 mb-3 flex shrink-0 items-start gap-2 rounded-[var(--radius-md)] border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
              <WifiOff className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>This order is safely stored offline. Kitchen and void actions unlock after sync; checkout remains available.</span>
            </div>
          )}

          {/* Action buttons */}
          {activeOrder && (
            <div className="shrink-0 flex flex-col gap-2 p-3 border-t border-[var(--color-border-subtle)] sm:flex-row">
              <Button
                variant="secondary"
                size="sm"
                disabled={pendingCount === 0 || isSyncPending}
                loading={sendKitchen.isPending}
                title={isSyncPending ? 'Available after this order syncs' : undefined}
                onClick={() => sendKitchen.mutate(activeOrder.documentId)}
                className="w-full justify-center sm:w-auto sm:flex-none"
              >
                <Send className="h-4 w-4" />
                Kitchen
                {pendingCount > 0 && (
                  <span className="ml-1 text-xs bg-[var(--color-accent-amber)]/20 text-[var(--color-accent-amber)] rounded-full px-1.5 py-0.5">
                    {pendingCount}
                  </span>
                )}
              </Button>
              <Button
                className="w-full justify-center sm:flex-1"
                disabled={orderItems.length === 0 || activeOrder.status === 'voided'}
                onClick={() => {
                  setCheckoutOrderDocumentId(activeOrder.documentId)
                  setShowCheckout(true)
                }}
              >
                <CreditCard className="h-4 w-4" />
                Pay · {money(grossTotal)}
              </Button>
            </div>
          )}
        </div>

        {/* ── Right: product catalogue ───────────────────────────────────── */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          <div className="shrink-0 flex flex-col gap-2 border-b border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">Menu</p>
              <p className="text-sm font-medium text-[var(--color-text-primary)]">
                {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--color-text-muted)] pointer-events-none" />
              <POSVisualKeyboardField
                value={search}
                onChange={setSearch}
                placeholder="Search items or categories..."
                inputClassName="pl-8 text-xs"
              />
            </div>
          </div>

          <div className="flex-1 flex min-h-0 min-w-0 flex-col xl:flex-row">
            <div
              data-testid="pos-category-rail"
              className="shrink-0 overflow-x-hidden overflow-y-auto border-b border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] p-2 xl:w-44 xl:border-b-0 xl:border-r"
            >
              <div className="grid min-w-0 grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-1">
                <CategoryFilterButton
                  label="All"
                  swatchClass="bg-[var(--color-accent-amber)]"
                  active={selectedCategoryId === null || isSearching}
                  onClick={() => {
                    setSelectedCategoryId(null)
                    if (isSearching) setSearch('')
                  }}
                />
                {categories.map((cat: any) => {
                  const color = catColorMap.get(cat.documentId) ?? 'bg-zinc-600 active:bg-zinc-500'
                  return (
                    <CategoryFilterButton
                      key={cat.documentId}
                      label={cat.name}
                      swatchClass={tileSwatch(color)}
                      active={selectedCategoryId === cat.documentId && !isSearching}
                      onClick={() => {
                        setSelectedCategoryId(cat.documentId)
                        if (isSearching) setSearch('')
                      }}
                    />
                  )
                })}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-3">
              {itemsLoading ? (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <Skeleton key={i} variant="rectangular" height={96} />
                  ))}
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="flex items-center justify-center h-full text-sm text-[var(--color-text-muted)]">
                  No items found
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5">
                  {filteredItems.map((item) => {
                    const color = item.category
                      ? (catColorMap.get(item.category.documentId) ?? 'bg-zinc-600 active:bg-zinc-500')
                      : 'bg-zinc-600 active:bg-zinc-500'
                    return (
                      <ProductTile
                        key={item.documentId}
                        item={item}
                        color={color}
                        money={money}
                        onTileClick={handleTileClick}
                      />
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Checkout modal ─────────────────────────────────────────────────── */}
      {checkoutOrderDocumentId && (
        <POSCheckoutModal
          open={showCheckout}
          onClose={() => {
            setShowCheckout(false)
            setCheckoutOrderDocumentId(null)
            if (!activeOrder) onBack()
          }}
          orderDocumentId={checkoutOrderDocumentId}
        />
      )}

      {/* ── Variant picker ─────────────────────────────────────────────────── */}
      <Modal
        open={!!variantModal}
        onClose={() => setVariantModal(null)}
        title={variantModal?.name ?? ''}
        size="sm"
      >
        {variantModal && (
          <div className="space-y-3">
            <div className="space-y-1">
              {variantModal.variants.map((v) => (
                <button
                  type="button"
                  key={v.documentId}
                  onClick={() => setPickedVariantId(v.documentId)}
                  className={[
                    'w-full flex items-center justify-between px-3 py-2.5 rounded-[var(--radius-md)] border text-sm transition-colors',
                    pickedVariantId === v.documentId
                      ? 'border-[var(--color-accent-amber)] bg-[var(--color-accent-amber)]/10 text-[var(--color-text-primary)]'
                      : 'border-[var(--color-border-default)] text-[var(--color-text-secondary)] hover:border-[var(--color-border-strong)]',
                  ].join(' ')}
                >
                  <span>{v.name}</span>
                  <span className="font-medium">
                    {money(variantModal.basePrice + v.priceDelta)}
                  </span>
                </button>
              ))}
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="secondary" size="sm" onClick={() => setVariantModal(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                className="flex-1"
                loading={addItem.isPending}
                onClick={handleVariantConfirm}
              >
                Add to order
              </Button>
            </div>
          </div>
        )}
      </Modal>
      <Modal
        open={!!weightEditorItem}
        onClose={() => setWeightEditorItem(null)}
        title={`Confirm weight${weightEditorItem ? ` · ${weightEditorItem.menuItem?.name ?? ''}` : ''}`}
        size="sm"
      >
        {weightEditorItem && (
          <div className="space-y-4">
            <p className="text-sm text-[var(--color-text-secondary)]">
              Enter the actual scale weight to confirm the final price.
            </p>
            <POSVisualNumpadField
              label="Actual weight (grams)"
              value={weightDraft}
              onChange={setWeightDraft}
              mode="integer"
              placeholder="e.g. 500"
              quickValues={[
                { label: '250 g', value: '250' },
                { label: '500 g', value: '500' },
                { label: '1 kg', value: '1000' },
              ]}
            />
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => setWeightEditorItem(null)}>Cancel</Button>
              <Button
                size="sm"
                className="flex-1"
                loading={updateItem.isPending}
                disabled={!Number.isInteger(Number(weightDraft)) || Number(weightDraft) < 1}
                onClick={() => updateItem.mutate(
                  { documentId: weightEditorItem.documentId, patch: { weightGrams: Number(weightDraft) } },
                  { onSuccess: () => setWeightEditorItem(null) },
                )}
              >
                Confirm weight
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}


